import { call, put, select, takeLatest, takeEvery } from "redux-saga/effects";
import { getRandomUUID } from "../../Token";
import { getBillingAccounts, fetchLicensesForAccount, changeLicenseType, removeLicense } from "../../configuration";
import axiosInstance from "../../global/axiosInterceptor";
import { getCookie, handleApiError } from "../../global/utility/Utils";
import { types } from "../actions";
import { LICENSE_NOTIFICATION_TYPES, LICENSE_NOTIFICATION_MESSAGES, LICENSE_ERROR_DESCRIPTIONS, LICENSE_ACTION_TYPES, LICENSE_SKU_PATTERNS, LICENSE_VALUES, VISION_SKUS_DISPLAYNAME, SKU_TO_APP_MAPPING, isV2Sku } from "../../global/constants/licensesConstants";
import { assignLicense, removeLicenseForFov, switchLicense } from "../../api/LicenseServiceManager";
import { resumeLicense } from "../../pages/Licenses/LicensesServices";
import { assignLicenseSuccess, assignLicenseFailure } from "../actions/license-actions";
import { changeLicenseTypeSuccess, setLicenseForbiddenError, showNotificationBanner, setLoading } from "../actions";

const activeSession = (state) => state.sessionReducer.activeSession;

function* fetchBillingAccountsData(navigate) {
  try {
    const response = yield axiosInstance.get(getBillingAccounts());

    if (response?.status === 200) {
      yield put({
        type: LICENSE_ACTION_TYPES.PUT_BILLING_ACCOUNTS_LICENSES,
        billingAccounts: response.data.currenttenancy.billingaccountids || [],
        ecpdId: response.data.currenttenancy.ecpdid || "",
        ecpdAccountName: response.data.currenttenancy.name || ""
      });
    } else {
      console.error("Error fetching billing accounts:", response.data);
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Error fetching billing accounts:", error);
    handleApiError("warning", errorMessage ? errorMessage : LICENSE_NOTIFICATION_MESSAGES.BILLING_ACCOUNTS_FETCH_FAILED);
    // Resolve loading state so license useEffect in NewApp can proceed (e.g. show no-license modal)
    yield put({
      type: LICENSE_ACTION_TYPES.PUT_BILLING_ACCOUNTS_LICENSES,
      billingAccounts: [],
      ecpdId: "",
      ecpdAccountName: ""
    });
  }
}

export function* fetchLicensesData(action) {
  // const session = yield select(activeSession);
  // const currentSession = session.activeSession;
  try {
    const response = yield axiosInstance.post(fetchLicensesForAccount(), {
      accountname: action?.payload?.accountId || action?.payload
    });

    if (response && Array.isArray(response.data)) {
      const isEaEnabled = window.REACT_APP_ENROLLED_APPEARANCE === "true";
      const isHsEnabled = window.REACT_APP_HISTORICAL_SEARCH_ENABLED === "true";
      const visionSkus = Object.values(LICENSE_SKU_PATTERNS);
      // Raw vision-scoped licenses, captured before EA/HS flag filtering, so a
      // real v2 entitlement is never masked by an unrelated flag being off.
      const visionLicensesRaw = response.data.filter((l) => visionSkus.includes(l.sku));
      const isV1OnlyAccount = visionLicensesRaw.length > 0 && !visionLicensesRaw.some((l) => isV2Sku(l.sku));
      const licenseList = response.data
        .filter((l) => {
          // Vision-only: sensor/ATS skus never enter this list so they can't
          // leak into any vision-portal UI (tables, radio options, etc.).
          if (!visionSkus.includes(l.sku)) return false;
          // Filter both v1 and v2 variants when feature flags are disabled
          if ((l.sku === LICENSE_SKU_PATTERNS.ENROLLED_APPEARANCE_V2) && !isEaEnabled) return false;
          if ((l.sku === LICENSE_SKU_PATTERNS.FORENSIC_SEARCH_V2) && !isHsEnabled) return false;
          return true;
        })
        .map((l) => {
          l.displayName = VISION_SKUS_DISPLAYNAME[l.sku];
          return l;
        });
      // Sensor/ATS licenses are kept separately (never merged into licenseList)
      // solely so NewApp's no-license modal can list the other apps the account
      // subscribes to.
      const otherAppLicenses = response.data.filter((l) => l.sku in SKU_TO_APP_MAPPING);
      if (action?.payload?.isFetchLicenseById) {
        yield put({
          type: LICENSE_ACTION_TYPES.PUT_LICENSES_OF_ACCOUNT_BY_ID,
          licenses: licenseList,
          origin: action?.payload?.origin || ""
        });
      } else {
        yield put({
          type: LICENSE_ACTION_TYPES.PUT_LICENSES_OF_ACCOUNT,
          licenses: licenseList,
          otherAppLicenses,
          isV1OnlyAccount,
          origin: action?.payload?.origin || ""
        });
      }
    } else if (!action?.payload?.isFetchLicenseById) {
      yield put({
        type: LICENSE_ACTION_TYPES.PUT_LICENSES_OF_ACCOUNT,
        licenses: [],
        origin: action?.payload?.origin || ""
      });
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    const status = error?.response?.status;

    if (status === 403) {
      yield put(setLicenseForbiddenError(true));
    }

    console.error(LICENSE_ERROR_DESCRIPTIONS.API_ERROR, error);
    handleApiError("warning", errorMessage ? errorMessage : LICENSE_NOTIFICATION_MESSAGES.LICENSES_FETCH_FAILED);

    // Mark licenses as loaded (empty) so NewApp's useEffect proceeds to the
    // no-license check. Without this, licensesLoaded stays false and the
    // Access Denied modal never appears on API failure/block.
    if (!action?.payload?.isFetchLicenseById) {
      yield put({
        type: LICENSE_ACTION_TYPES.PUT_LICENSES_OF_ACCOUNT,
        licenses: [],
        origin: action?.payload?.origin || ""
      });
    }
  }
}

function* changeLicenseTypeSaga(action) {
  const { billingAccountId, deviceIdentifier, assignSKU, removeSKU, navigate, fovList, successTitle } = action.payload;

  try {
    let response;
    if (assignSKU === LICENSE_VALUES.NO_LICENSE) {
      response = yield axiosInstance.post(removeLicense(), {
        accountname: billingAccountId,
        deviceidentifier: {
          deviceid: deviceIdentifier
        },
        sku: removeSKU
      });
    } else {
      response = yield axiosInstance.post(changeLicenseType(), {
        accountname: billingAccountId,
        deviceidentifier: {
          deviceid: deviceIdentifier
        },
        assignsku: assignSKU,
        removesku: removeSKU
      });
    }

    if (response?.status === 200) {
      yield put(changeLicenseTypeSuccess(response));
      yield call(fetchLicensesData, { payload: billingAccountId });
      // Keep licensesinfo in sync with licenses so downstream consumers
      // (deploy payload, ROI page, camera tab) always read the correct SKU
      // without requiring a full page refresh.
      const updatedFovList = fovList.map((fov) => {
        if (fov.id === deviceIdentifier && (fov.licenses || []).includes(removeSKU)) {
          return {
            ...fov,
            licenses: (fov.licenses || []).map((license) => (license === removeSKU ? assignSKU : license)),
            licensesinfo: fov.licensesinfo
              ? assignSKU === LICENSE_VALUES.NO_LICENSE
                ? fov.licensesinfo.filter((lic) => lic.sku !== removeSKU)
                : fov.licensesinfo.map((lic) => lic.sku === removeSKU ? { ...lic, sku: assignSKU } : lic)
              : fov.licensesinfo,
          };
        }
        return fov;
      });

      yield put({
        type: types.UPDATE_FOV_UPDATED_LIST,
        fovList: updatedFovList
      });

      yield put(showNotificationBanner({
        type: LICENSE_NOTIFICATION_TYPES.LICENSE_CHANGE_SUCCESS,
        priority: "success",
        data: {
          title: successTitle || (assignSKU === LICENSE_VALUES.NO_LICENSE ? LICENSE_NOTIFICATION_MESSAGES.LICENSE_REMOVED_SUCCESS : LICENSE_NOTIFICATION_MESSAGES.LICENSE_CHANGED_SUCCESS)
        },
        showNotification: true
      }));
    } else {
      yield put(showNotificationBanner({
        type: LICENSE_NOTIFICATION_TYPES.CHANGE_LICENSE_FAILED,
        priority: "critical",
        data: {
          title: LICENSE_NOTIFICATION_MESSAGES.LICENSE_SWITCH_FAILED
      },
        showNotification: true
      }));
      yield put({
        type: types.CHANGE_LICENSE_TYPE_FAILURE,
        error: response
      });
    }
  } catch (error) {
    const userFriendlyMessage = error?.response?.data?.error_description === LICENSE_ERROR_DESCRIPTIONS.INCOMPATIBLE_SKU
    ? LICENSE_NOTIFICATION_MESSAGES.INCOMPATIBLE_SKU_ERROR
    : assignSKU === LICENSE_VALUES.NO_LICENSE ? LICENSE_NOTIFICATION_MESSAGES.LICENSE_REMOVE_FAILED : LICENSE_NOTIFICATION_MESSAGES.LICENSE_SWITCH_FAILED;
    console.error("Error changing license type:", error);
    yield put(showNotificationBanner({
      type: LICENSE_NOTIFICATION_TYPES.CHANGE_LICENSE_FAILED,
      priority: "critical",
      data: {
      title: userFriendlyMessage
      },
      showNotification: true
      }));
    // Dispatch failure action so UI can respond
    yield put({
      type: types.CHANGE_LICENSE_TYPE_FAILURE,
      error: error
    });
    
    return [];
  }
}

/**
 * Runs multiple HS license operations in parallel, then:
 *  1. Patches deviceReducer.fovList optimistically (same as changeLicenseTypeSaga does for Settings)
 *  2. Re-fetches account-level licenseReducer.licenses
 *  3. Calls payload.resolve() so the calling component can await completion
 */
function* batchLicenseActionsSaga(action) {
  const { actions, accountId, resolve, suppressBanners } = action.payload;
  const HS_SKU = LICENSE_SKU_PATTERNS.FORENSIC_SEARCH_V2;

  // Nothing to do — resolve immediately without any API calls
  if (!actions?.length) {
    resolve({ anyFailed: false });
    return;
  }

  // Read the latest fovList from the store — using yield select here (not a closure
  // from the component) ensures we see any FOVs that were added by a preceding
  // dispatch(updateDevices(...)) before this saga ran.
  const fovList = yield select((state) => state.deviceReducer.fovList);

  // ── 1. Fire all API calls in parallel ───────────────────────────────────────
  const results = yield call(() =>
    Promise.allSettled(
      actions.map(({ fovId, action: actionType, currentSku }) => {
        if (actionType === "resume") return resumeLicense(accountId, fovId, HS_SKU);
        if (actionType === "remove") return removeLicenseForFov({ accountname: accountId, deviceid: fovId, sku: HS_SKU });
        if (actionType === "switch") return switchLicense({ accountname: accountId, deviceid: fovId, assignSku: HS_SKU, removeSku: currentSku });
        return assignLicense({ accountname: accountId, deviceid: fovId, sku: HS_SKU });
      })
    )
  );

  // ── 2. Dispatch a banner for every failure ───────────────────────────────────
  // suppressBanners callers (e.g. onboarding flow) show their own combined banner.
  const anyFailed = results.some((r) => r.status === "rejected");
  if (!suppressBanners) {
    for (let i = 0; i < results.length; i++) {
      if (results[i].status === "rejected") {
        const actionType = actions[i].action;
        const type =
          actionType === "resume" ? "Resume Search Failed"
          : actionType === "remove" ? "Disable Search Failed"
          : "Enable Search Failed";
        yield put(showNotificationBanner({ type, priority: "critical", showNotification: true }));
      }
    }
  }

  // ── 3. Patch deviceReducer.fovList for successful actions ────────────────────
  // Mirrors what changeLicenseTypeSaga does so downstream consumers (camera info,
  // deploy payload, ROI page) see the updated licensesinfo without a page reload.
  const successfulActions = actions.filter((_, i) => results[i].status === "fulfilled");
  if (successfulActions.length > 0 && fovList?.length) {
    const updatedFovList = fovList.map((fov) => {
      const a = successfulActions.find((sa) => sa.fovId === fov.id);
      if (!a) return fov;

      if (a.action === "assign") {
        return {
          ...fov,
          licenses: [...(fov.licenses || []).filter((l) => l !== HS_SKU), HS_SKU],
          licensesinfo: [
            ...(fov.licensesinfo || []).filter((l) => l.sku !== HS_SKU),
            { sku: HS_SKU, status: "active" },
          ],
        };
      }
      if (a.action === "resume") {
        return {
          ...fov,
          licensesinfo: (fov.licensesinfo || []).map((l) =>
            l.sku === HS_SKU ? { ...l, status: "active" } : l
          ),
        };
      }
      if (a.action === "remove") {
        return {
          ...fov,
          licenses: (fov.licenses || []).filter((l) => l !== HS_SKU),
          licensesinfo: (fov.licensesinfo || []).filter((l) => l.sku !== HS_SKU),
        };
      }
      if (a.action === "switch") {
        return {
          ...fov,
          licenses: (fov.licenses || []).map((l) => (l === a.currentSku ? HS_SKU : l)),
          licensesinfo: (fov.licensesinfo || []).map((l) =>
            l.sku === a.currentSku ? { ...l, sku: HS_SKU, status: "active" } : l
          ),
        };
      }
      return fov;
    });
    yield put({ type: types.UPDATE_FOV_UPDATED_LIST, fovList: updatedFovList });
  }

  // ── 4. Refresh account-level license pool count ──────────────────────────────
  yield call(fetchLicensesData, { payload: { accountId } });

  // ── 5. Signal completion to the calling component ───────────────────────────
  resolve({ anyFailed });
}

function* assignLicenseSaga(action) {
  try {
    const session = yield select(activeSession);
    yield put(setLoading(true));
    const response = yield call(assignLicense, action?.payload, session.activeSession);

    if (response) {
      const navigate = action?.payload?.navigate;
      const stateData = action?.payload?.stateData;
      yield put(assignLicenseSuccess(response.data));

      yield put(showNotificationBanner({
        type: "Assign License Successful",
        priority: "success",
        data: {
          title: LICENSE_NOTIFICATION_MESSAGES.LICENSE_ASSIGN_SUCCESS
        },
        showNotification: true
      }));

      if (typeof navigate === "function") {
        navigate("/cvAppSub", { state: stateData });
      }
    } else {
      yield put(showNotificationBanner({
        type: "Assign License Failed",
        priority: "critical",
        data: {
          title: LICENSE_NOTIFICATION_MESSAGES.LICENSE_ASSIGN_FAILURE
        },
        showNotification: true
      }));
      yield put(assignLicenseFailure(response.data))
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || "Unknown error occurred";
    yield put(showNotificationBanner({
      type: "Assign License Failed",
      priority: "critical",
      data: {
        title: LICENSE_NOTIFICATION_MESSAGES.LICENSE_ASSIGN_FAILURE + ", " + errorMessage
      },
      showNotification: true
    }));
    yield(assignLicenseFailure(LICENSE_NOTIFICATION_MESSAGES.LICENSE_ASSIGN_FAILURE));
  } finally {
    yield put(setLoading(false));
  }
}

function* LicenseSaga() {
  yield takeLatest(LICENSE_ACTION_TYPES.FETCH_BILLING_ACCOUNTS_LICENSES, fetchBillingAccountsData);
  yield takeLatest(LICENSE_ACTION_TYPES.FETCH_LICENSES_OF_ACCOUNT, fetchLicensesData);
  yield takeLatest(LICENSE_ACTION_TYPES.FETCH_LICENSES_OF_ACCOUNT_BY_ID, fetchLicensesData);
  yield takeLatest(LICENSE_ACTION_TYPES.CHANGE_LICENSE_TYPE, changeLicenseTypeSaga);
  yield takeLatest(LICENSE_ACTION_TYPES.ASSIGN_LICENSE, assignLicenseSaga);
  yield takeEvery(LICENSE_ACTION_TYPES.BATCH_LICENSE_ACTIONS, batchLicenseActionsSaga);
}

export default LicenseSaga;
